/* eslint-disable react-refresh/only-export-components */
import { useState } from "react";
import { useTranslate } from "ra-core";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ENCHARGE_BRANDS } from "./encharge/instances";
import type { EnchargeInstance } from "./encharge/types";
import { EmailListsTable } from "./EmailListsTable";

export const EmailListsPage = () => {
  const translate = useTranslate();
  const [activeInstance, setActiveInstance] = useState<EnchargeInstance>(
    "gobo-dk",
  );

  const isDemo = import.meta.env.VITE_IS_DEMO === "true";

  if (isDemo) {
    return (
      <div className="max-w-4xl mx-auto mt-8 px-4">
        <Card>
          <CardHeader>
            <CardTitle>
              {translate("crm.email_lists.title", { _: "Email lists" })}
            </CardTitle>
          </CardHeader>
          <CardContent className="text-muted-foreground">
            {translate("crm.email_lists.demo_unavailable", {
              _: "Email lists require Encharge configuration and are not available in demo mode.",
            })}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    // Mobile: shell has no MobileContent — keep px-4 + bottom nav clearance.
    // Desktop: Layout already provides px-4, so avoid double gutters (md:px-0).
    <div className="mt-4 px-4 pb-28 md:px-0 md:pb-8">
      {/* Mobile: no card chrome (closer to Companies). md+: keep Card. */}
      <div className="md:bg-card md:text-card-foreground md:flex md:flex-col md:gap-6 md:rounded-xl md:border md:py-6">
        <div className="pb-0 md:px-6">
          <h1 className="leading-none font-semibold text-base">
            {translate("crm.email_lists.title", { _: "Email lists" })}
          </h1>
          <p className="text-sm text-muted-foreground mt-1.5">
            {translate("crm.email_lists.description", {
              _: "View and manage Encharge mailing list subscriptions across Gobo brands.",
            })}
          </p>
        </div>
        <div className="pt-6 md:px-6">
          <Tabs
            value={activeInstance}
            onValueChange={(value) =>
              setActiveInstance(value as EnchargeInstance)
            }
          >
            <TabsList className="w-full flex flex-wrap h-auto gap-1 justify-start">
              {ENCHARGE_BRANDS.map((brand) => (
                <TabsTrigger key={brand.id} value={brand.id}>
                  {translate(brand.labelKey, { _: brand.fallback })}
                </TabsTrigger>
              ))}
            </TabsList>
            {ENCHARGE_BRANDS.map((brand) => (
              <TabsContent key={brand.id} value={brand.id} className="mt-6">
                <div className="mb-4">
                  <h2 className="text-lg font-medium">
                    {translate("crm.email_lists.brand_title", {
                      brand: translate(brand.labelKey, { _: brand.fallback }),
                      _: "%{brand} contacts",
                    })}
                  </h2>
                  <p className="text-sm text-muted-foreground">
                    {translate("crm.email_lists.brand_description", {
                      brand: translate(brand.labelKey, { _: brand.fallback }),
                      _: "Mailing list contacts for %{brand}.",
                    })}
                  </p>
                </div>
                <EmailListsTable instance={brand.id} />
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </div>
    </div>
  );
};

EmailListsPage.path = "/email-lists";
